/**
 * @file Builds the description rows of an investment, delegating to its profile-specific renderer.
 */
import React from "react";
import keyBy from "lodash.keyby";
import { Space, Typography } from "antd";

import { Value } from "@components/Text";

import {
  INVESTMENT_PROFILE_TYPE_JOINT,
  INVESTMENT_PROFILE_TYPE_SDIRA,
  INVESTMENT_PROFILE_TYPE_ENTITY,
  INVESTMENT_PROFILE_TYPE_INDIVIDUAL,
} from "@components/Domain";

import renderProfileSdira from "./renderProfileSdira";
import renderProfileJoint from "./renderProfileJoint";
import renderProfileEntity from "./renderProfileEntity";
import renderProfileIndividual from "./renderProfileIndividual";
import type { DescriptionItem, Investment, Tier } from "./types";

const { Text } = Typography;

const LABEL_CLASS = "Class";
const LABEL_PROFILE_TYPE = "Profile Type";
const LABEL_INVESTMENTS = "Investments";

/**
 * Builds the description rows of an investment, delegating to the renderer of its profile type.
 */
const renderInvestmentItems = (tiers: Tier[], investment: Investment): DescriptionItem[] => {
  const tiersMap: Record<string, Tier> = keyBy(tiers, "name");

  const {
    investor,
    investments,
    profileType,
    profileJoint,
    profileSdira,
    profileEntity,
    profileIndividual,
  } = investment;

  /**
   * Labels a sub-investment by its tier title, or by its tier class name.
   */
  const getTierLabel = (projectTierId: string): string => {
    const tier = tiersMap[projectTierId];
    const { title, name } = tier;

    return title || `${LABEL_CLASS} ${name}`;
  };

  const formattedInvestments = (
    <Space
      size={0}
      direction="vertical"
    >
      {investments.map(({ projectTierId, amount }, key) => (
        <Text key={key}>
          {getTierLabel(projectTierId)} - <Value value={amount} />
        </Text>
      ))}
    </Space>
  );

  const isIndividual =
    profileType === INVESTMENT_PROFILE_TYPE_INDIVIDUAL && !!profileIndividual;

  if (isIndividual) {
    const items = renderProfileIndividual(profileIndividual, formattedInvestments, investor);

    return items;
  }

  const isJoint =
    profileType === INVESTMENT_PROFILE_TYPE_JOINT && !!profileJoint;

  if (isJoint) {
    const items = renderProfileJoint(profileJoint, formattedInvestments, investor);

    return items;
  }

  const isSdira =
    profileType === INVESTMENT_PROFILE_TYPE_SDIRA && !!profileSdira;

  if (isSdira) {
    const items = renderProfileSdira(profileSdira, formattedInvestments, investor);

    return items;
  }

  const isEntity =
    profileType === INVESTMENT_PROFILE_TYPE_ENTITY && !!profileEntity;

  if (isEntity) {
    const items = renderProfileEntity(profileEntity, formattedInvestments);

    return items;
  }

  return [
    { label: LABEL_PROFILE_TYPE, value: profileType },
    { label: LABEL_INVESTMENTS, value: formattedInvestments },
  ];
};

export default renderInvestmentItems;
